import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Upload,
    FileSpreadsheet,
    CheckCircle2,
    XCircle
} from "lucide-react";

import api from "../services/api";


function ImportPage() {
    const navigate = useNavigate();

    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    const [permissions, setPermissions] = useState([]);
    const [userRole, setUserRole] = useState("");
    const [accessLoading, setAccessLoading] = useState(true);

    const [showErrors, setShowErrors] = useState(false);
    const [importErrors, setImportErrors] = useState([]);
    const [loadingErrors, setLoadingErrors] = useState(false);


    const hasPermission = (key) => {
        if (userRole === "OWNER") {
            return true;
        }

        return permissions.some(
            item => item.permission_key === key
        );
    };


    useEffect(() => {
        const loadAccess = async () => {
            try {
                const response = await api.get("/auth/me");

                setUserRole(
                    response.data.user?.role || ""
                );

                setPermissions(
                    response.data.permissions || []
                );

            } catch (error) {
                setError(
                    "Unable to verify permissions"
                );

            } finally {
                setAccessLoading(false);
            }
        };

        loadAccess();
    }, []);


    const handleFileChange = (e) => {
        const selectedFile =
            e.target.files[0];

        setError("");
        setResult(null);

        if (!selectedFile) {
            setFile(null);
            return;
        }

        const allowedExtensions = [
            ".xlsx",
            ".xls"
        ];

        const fileName =
            selectedFile.name.toLowerCase();

        const validFile =
            allowedExtensions.some(
                extension =>
                    fileName.endsWith(
                        extension
                    )
            );

        if (!validFile) {
            setFile(null);

            setError(
                "Please select an Excel file (.xlsx or .xls)."
            );

            return;
        }

        setFile(selectedFile);
    };


    const handleUpload = async () => {
        if (!file) {
            setError(
                "Please select an Excel file first."
            );

            return;
        }

        try {
            setUploading(true);
            setError("");
            setResult(null);
            setShowErrors(false);
            setImportErrors([]);

            const formData =
                new FormData();

            formData.append(
                "file",
                file
            );

            const response =
                await api.post(
                    "/import/excel",
                    formData,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data"
                        }
                    }
                );

            setResult(
                response.data
            );

        } catch (error) {
            setError(
                error.response?.data?.message ||
                "Excel import failed"
            );

        } finally {
            setUploading(false);
        }
    };


    const loadImportErrors = async () => {
        if (
            !result?.import_id ||
            !result?.summary?.failed_rows
        ) {
            return;
        }

        try {
            setLoadingErrors(true);
            setError("");

            const response =
                await api.get(
                    `/history/imports/${result.import_id}/errors`
                );

            setImportErrors(
                response.data.errors || []
            );

            setShowErrors(true);

        } catch (error) {
            setError(
                error.response?.data?.message ||
                "Unable to load import errors"
            );

        } finally {
            setLoadingErrors(false);
        }
    };


    if (accessLoading) {
        return (
            <div className="mobile-app">
                <div className="loading-state">
                    Loading...
                </div>
            </div>
        );
    }


    if (!hasPermission("UPLOAD_EXCEL")) {
        return (
            <div className="mobile-app">

                <header className="page-header">

                    <button
                        className="back-button"
                        onClick={() =>
                            navigate(
                                "/dashboard"
                            )
                        }
                    >
                        <ArrowLeft
                            size={21}
                        />
                    </button>

                    <div>
                        <h1>
                            Import
                        </h1>

                        <p>
                            Excel price list
                        </p>
                    </div>

                </header>


                <div className="permission-denied-page">

                    <XCircle size={44} />

                    <h2>
                        Access Denied
                    </h2>

                    <p>
                        You do not have
                        permission to upload
                        Excel files.
                    </p>

                </div>

            </div>
        );
    }


    return (
        <div className="mobile-app">

            <header className="page-header">

                <button
                    className="back-button"
                    onClick={() =>
                        navigate(
                            "/dashboard"
                        )
                    }
                >
                    <ArrowLeft
                        size={21}
                    />
                </button>

                <div>
                    <h1>
                        Import Products
                    </h1>

                    <p>
                        Upload Excel price list
                    </p>
                </div>

            </header>


            <main className="import-content">

                <section className="import-card">

                    <div className="import-icon">
                        <FileSpreadsheet
                            size={30}
                        />
                    </div>

                    <h2>
                        Upload Excel File
                    </h2>

                    <p>
                        Select your product
                        price list in .xlsx
                        or .xls format.
                    </p>


                    <label className="file-picker">

                        <Upload size={20} />

                        <span>
                            {file
                                ? file.name
                                : "Choose Excel File"
                            }
                        </span>

                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={
                                handleFileChange
                            }
                        />

                    </label>


                    <button
                        className="primary-button import-button"
                        onClick={handleUpload}
                        disabled={
                            !file ||
                            uploading
                        }
                    >
                        {uploading
                            ? "Importing..."
                            : "Import Excel"
                        }
                    </button>

                </section>


                {error && (
                    <div className="error-message import-error">
                        {error}
                    </div>
                )}


                {result && (

                    <section className="import-result-card">

                        <div className="import-success-header">

                            <CheckCircle2
                                size={24}
                            />

                            <div>
                                <h3>
                                    Import Complete
                                </h3>

                                <p>
                                    {result.message}
                                </p>
                            </div>

                        </div>


                        <div className="import-summary-grid">

                            <div>
                                <span>
                                    Total Rows
                                </span>

                                <strong>
                                    {
                                        result.summary
                                            ?.total_rows
                                    }
                                </strong>
                            </div>


                            <div>
                                <span>
                                    Added
                                </span>

                                <strong>
                                    {
                                        result.summary
                                            ?.products_added
                                    }
                                </strong>
                            </div>


                            <div>
                                <span>
                                    Updated
                                </span>

                                <strong>
                                    {
                                        result.summary
                                            ?.products_updated
                                    }
                                </strong>
                            </div>


                            <div>
                                <span>
                                    Unchanged
                                </span>

                                <strong>
                                    {
                                        result.summary
                                            ?.products_unchanged
                                    }
                                </strong>
                            </div>


                            <button
                                type="button"
                                className="failed-summary failed-summary-button"
                                onClick={
                                    loadImportErrors
                                }
                                disabled={
                                    !result.summary
                                        ?.failed_rows ||
                                    loadingErrors
                                }
                            >
                                <span>
                                    Failed
                                </span>

                                <strong>
                                    {
                                        result.summary
                                            ?.failed_rows
                                    }
                                </strong>

                                {
                                    result.summary
                                        ?.failed_rows > 0 && (
                                        <small>
                                            {
                                                loadingErrors
                                                    ? "Loading..."
                                                    : "Tap to view"
                                            }
                                        </small>
                                    )
                                }

                            </button>

                        </div>

                    </section>

                )}

            </main>


            {showErrors && (

                <div
                    className="import-errors-overlay"
                    onClick={() =>
                        setShowErrors(false)
                    }
                >

                    <div
                        className="import-errors-modal"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <div className="import-errors-header">

                            <div>
                                <p>
                                    Import Issues
                                </p>

                                <h2>
                                    {
                                        importErrors.length
                                    }{" "}
                                    Failed Rows
                                </h2>
                            </div>


                            <button
                                type="button"
                                onClick={() =>
                                    setShowErrors(
                                        false
                                    )
                                }
                            >
                                ×
                            </button>

                        </div>


                        <div className="import-errors-list">

                            {importErrors.map(
                                (item) => (

                                    <div
                                        className="import-error-item"
                                        key={
                                            item.id
                                        }
                                    >

                                        <div className="import-error-row">
                                            Row{" "}
                                            {
                                                item.row_number
                                            }
                                        </div>

                                        <strong>
                                            {
                                                item.product_name ||
                                                "Unknown Product"
                                            }
                                        </strong>

                                        <p>
                                            {
                                                item.error_message
                                            }
                                        </p>

                                        {
                                            item.raw_price !==
                                                null && (
                                                <small>
                                                    Value:{" "}
                                                    {
                                                        item.raw_price
                                                    }
                                                </small>
                                            )
                                        }

                                    </div>

                                )
                            )}

                        </div>

                    </div>

                </div>

            )}

        </div>
    );
}


export default ImportPage;